import { Region } from '@heidi/schema/ml-scribe';

/**
 * Stored in `evidence_settings.country` when a clinician opts out of regional
 * prioritization (EVI-2318). Web persists this sentinel; ml-scribe honours it
 * in evidence location resolution (`resolve_evidence_country`, ml-scribe#17205).
 * The picker is gated by `release-evidence-international-country-option` and
 * hidden in medical-device-regulated countries (EU/UK including SE_AZ1).
 *
 * MOBILE-PARITY-DEBT(EVI-2318): web persists country="International" as a
 * no-region opt-out. Backend already ships ml-scribe#17205. iOS/Android must
 * honour the same sentinel — shared ticket EVI-2318, owners Evidence + mobile
 * pods, until dedicated APP tickets exist. Remove when native clients read the
 * sentinel (or a first-class international field) and this web special-case
 * can be deleted.
 */
export const INTERNATIONAL_EVIDENCE_COUNTRY = 'International';

/**
 * Kinde regions where Evidence is a medical device. Matches
 * UNIFIED_COLLECTIONS_BLOCKED_REGIONS (UK/EU/SE_AZ1). Fail closed: an unknown
 * or unresolved region is treated as blocked by `honourInternationalEvidenceCountry`.
 */
export const MEDICAL_DEVICE_EVIDENCE_REGIONS: ReadonlySet<Region> = new Set([
  Region.UK,
  Region.EU,
  Region.SE_AZ1,
]);

interface RegionGatedEvidenceFeatureArgs {
  flagEnabled: boolean;
  region: Region | undefined;
  isPending: boolean;
  isError: boolean;
}

/** Flag on, Kinde region resolved, and that region is not a medical-device one. */
function isRegionGatedEvidenceFeatureOn(args: RegionGatedEvidenceFeatureArgs): boolean {
  if (!args.flagEnabled) return false;
  if (args.isPending || args.isError || !args.region) return false;
  return !MEDICAL_DEVICE_EVIDENCE_REGIONS.has(args.region);
}

export function honourInternationalEvidenceCountry(
  args: RegionGatedEvidenceFeatureArgs
): boolean {
  return isRegionGatedEvidenceFeatureOn(args);
}

/**
 * Whether Key Safety Notes render muted (neutral chrome, model-emitted icons
 * stripped) instead of the certified amber block. Same fail-closed gate as the
 * International option: the certified block stays for UK/EU/SE_AZ1 and for any
 * unresolved region. `region` must be the backend Kinde region — never the
 * browser-inferred one.
 */
export function shouldMuteKeySafetyNotes(args: RegionGatedEvidenceFeatureArgs): boolean {
  return isRegionGatedEvidenceFeatureOn(args);
}

export function isInternationalEvidenceCountry(
  country: string | null | undefined
): boolean {
  return country === INTERNATIONAL_EVIDENCE_COUNTRY;
}
